<script lang="ts" setup>
import { watch, computed, useTemplateRef } from "vue"
import { useI18n } from "vue-i18n"
import * as yup from "yup"
import { useField, useForm } from "vee-validate"
import { DAYS } from "@modules/irve/constants"

type Props = {
    /**
     * Affiche où non le bouton de suppression de la ligne
     */
    showButton: boolean
    /**
     * Dictionnaire des périodes désactivées par jour. La clé est le jour et la valeur est un tableau d'heures désactivées.
     * Exemple: { "Lundi": [{start: 2, end: 5}, {start: 6, end: 10}] }
     */
    disabledPeriods?: Record<string, { start: number; end: number }[]>
    /**
     * Liste des périodes sélectionnées
     */
    periods: { start: number; end: number }[]
    /**
     * Foisonnement
     */
    foisonnement: number | undefined
    /**
     * Jours sélectionnés
     */
    days: string[]
}

defineEmits([
    "click:remove",
    "update:periods",
    "update:foisonnement",
    "update:days",
])

const props = withDefaults(defineProps<Props>(), {
    showButton: true,
    disabledPeriods: () => {
        return {}
    },
    days: () => [],
})

/*=============================================================================*/
/*                                  Lang                                       */
/*=============================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            period: {
                title: "Période",
                placeholder: "-- : -- - --:--",
                hint: "Sélectionner un ou plusieurs jours pour ajouter des périodes",
                tooltip:
                    "En heures, les plages horaires du profil d’occupation des bornes.",
            },
            foisonnement: {
                title: "Foisonnement",
                tooltip:
                    "Taux moyen d’occupation des bornes pendant la plage horaire renseignée.",
            },
            days: {
                title: "Séléctionnez des jours",
                disabled:
                    "Certaines dates sont désactivée\ncar des périodes similaires sont déjà définies",
            },
            error: {
                foisonnementMax: "La valeur doit être comprise entre 0 et 100",
            },
        },
    },
})

// ============================================================================
// Gestion des périodes sélectionnées
// =============================================================================
/**
 * Référence vers le composant BaseMultiSelectPeriod
 */
const multiselectRef = useTemplateRef("multiselectRef")

/**
 * Liste des périodes désactivées en fonction des jours sélectionnés
 */
const getDisabledPeriods = computed(() => {
    // si aucun jour n'est sélectionné ou si aucune période n'est désactivée
    if (
        !Object.values(props.disabledPeriods).length ||
        !modelDays.value.length
    ) {
        return []
    }
    // retourne les périodes désactivées qui match les jours sélectionnés
    const value = Object.entries(props.disabledPeriods).reduce(
        (acc: { start: number; end: number }[], [day, value]) => {
            if (modelDays.value.includes(day)) {
                acc.push(...value)
            }
            return acc
        },
        []
    )
    return value
})

/**
 * Récupère les options des jours pour le sélecteur
 * en désactivant les jours qui ont déjà des périodes sélectionnées
 */
const getDaysOptions = computed(() => {
    return DAYS.map((day) => {
        const _day = day.toLowerCase()

        let disabled = false

        // récupère la journée dans la liste des périodes désactivées
        const dayInDisabledPeriod = props.disabledPeriods[_day]

        // Dans le cas où la date est déjà sélectionnée quelque part d'autre,
        // on va vérifier que les périodes ne se superposent pas
        if (dayInDisabledPeriod && !modelDays.value.includes(_day)) {
            // Pour chaque période de la journée, vérifie si une période n'est pas superposée
            dayInDisabledPeriod.forEach((period) => {
                if (period.start === 0 && period.end === 0) {
                    disabled = true
                }
                // Vérifie qu'une période dans
                const hasSuperposingPeriod = modelPeriods.value.find(
                    ({ start, end }) => {
                        if (start < period.end && end > period.start) {
                            return true
                        }

                        if (start === 0 && end === 0) {
                            return true
                        }

                        return false
                    }
                )

                if (hasSuperposingPeriod) {
                    disabled = true
                }
            })
        }

        return {
            label: day,
            value: _day,
            disabled,
        }
    })
})

// ================================================================
// Gestion du formulaire
// ================================================================

useForm()

/**
 * Liste des jours sélectionnées
 */
const {
    errorMessage: errorDays,
    value: modelDays,
    validate: validateDays,
} = useField<string[]>(
    "days",
    yup
        .array()
        .of(yup.string())
        .default(props.days)
        .min(1, "Veuillez sélectionner au moins un jour")
        .required("Veuillez sélectionner au moins un jour"),
    {
        syncVModel: "days",
    }
)

//========================================================
//                   Computed
//========================================================
const getFoisonnement = computed({
    get: () => {
        return modelFoisonnement.value !== undefined
            ? Number((modelFoisonnement.value * 100).toFixed(2))
            : 0
    },
    set: (value) => {
        modelFoisonnement.value =
            value !== undefined ? Number((value / 100).toFixed(4)) : 0
    },
})

/**
 * Liste des périodes sélectionnées
 */
const {
    value: modelPeriods,
    errorMessage: errorPeriods,
    validate: validatePeriods,
} = useField<{ start: number; end: number }[]>(
    "periods",
    yup
        .array()
        .of(
            yup.object({
                start: yup.number().required(),
                end: yup.number().required(),
            })
        )
        .default([])
        .min(1, "Veuillez renseigner au moins une période")
        .required("Veuillez renseigner au moins une période"),
    {
        syncVModel: "periods",
    }
)

/**
 * Le foisonnement associé
 */
const {
    errorMessage: errorFoisonnement,
    value: modelFoisonnement,
    validate: validateFoisonnement,
} = useField<number>(
    "foisonnement",
    yup.number().required("Le foisonnement est requis"),
    {
        syncVModel: "foisonnement",
    }
)

defineExpose({
    validate: async () => {
        // valide chaque useField
        const valid =
            (await validateDays()).valid &&
            (await validateFoisonnement()).valid &&
            (await validatePeriods()).valid

        // valide le sélecteur de pérides car contient un formulaire
        if (multiselectRef.value) {
            return valid && (await multiselectRef.value.validate())
        }
        return valid
    },
    /**
     * Retourne les périodes désactivées pour cette ligne
     */
    getDisabledPeriods: () => {
        return modelDays.value.reduce(
            (acc: Record<string, { start: number; end: number }[]>, day) => {
                if (!acc[day]) {
                    acc[day] = []
                }
                acc[day] = [...modelPeriods.value]
                return acc
            },
            {}
        )
    },
})

watch(modelDays, (value) => {
    if (!value.length) {
        modelPeriods.value = []
    }
})
</script>

<template>
    <form
        novalidate
        class="utilisation-profile-form-row grid w-full grid-cols-[repeat(3,minmax(0,1fr))_auto] items-start gap-4"
    >
        <!-- Sélecteur de date -->
        <BaseInputWrapper
            input-id="days-select"
            :label="t('days.title')"
            :error="errorDays"
        >
            <BaseMultiSelect
                id="days-select"
                v-model="modelDays"
                :options="getDaysOptions"
                option-label="label"
                option-disabled="disabled"
                option-value="value"
                :show-toggle-all="false"
                display="chip"
                :invalid="!!errorDays"
            >
                <template #footer>
                    <BaseMessage
                        severity="info"
                        class="m-2"
                        size="small"
                        v-if="getDaysOptions.some((day) => day.disabled)"
                    >
                        <span class="whitespace-pre-wrap">{{
                            t("days.disabled")
                        }}</span>
                    </BaseMessage>
                </template>
            </BaseMultiSelect>
        </BaseInputWrapper>
        <!-- Sélecteur de périodes -->
        <BaseMultiSelectPeriod
            :model-value="modelPeriods"
            :label="t('period.title')"
            :disabled="!modelDays.length"
            :disabled-periods="getDisabledPeriods"
            ref="multiselectRef"
            required
            :error="errorPeriods"
            auto-select
            v-tooltip="t('period.tooltip')"
            :hint="!modelDays.length ? t('period.hint') : ''"
            @update:model-value="
                $emit('update:periods', JSON.parse(JSON.stringify($event)))
            "
        />
        <!-- Sélecteur du foisonnnement -->
        <div class="flex flex-row gap-4">
            <BaseInputWrapper
                input-id="foisonnement-input"
                :label="t('foisonnement.title')"
                :error="errorFoisonnement"
                v-tooltip="t('foisonnement.tooltip')"
            >
                <BaseInputNumber
                    for="foisonnement-input"
                    v-model="getFoisonnement"
                    :invalid="!!errorFoisonnement"
                    :max="100"
                    :min="0"
                    :step="0.01"
                    :min-fraction-digits="2"
                    :max-fraction-digits="2"
                    :disabled="!modelDays.length"
                />
                <template #suffix>
                    <BaseInputGroupAddon>
                        <i class="pi pi-percentage text-xs"></i>
                    </BaseInputGroupAddon>
                </template>
            </BaseInputWrapper>
            <!-- Bouton supprimer -->
            <BaseButton
                v-if="showButton"
                icon="pi pi-trash"
                severity="secondary"
                rounded
                class="w-14 shrink-0"
                text
                @click="$emit('click:remove')"
            ></BaseButton>
        </div>
    </form>
</template>
